/**
 * Four-part App Store version names for asset-only rebuilds (SCH-2593).
 *
 * PROVISIONAL. Decided 2026-10-02; App Store Connect accepted `1.9.1.1` as a
 * version record name, but attaching a build whose `CFBundleShortVersionString`
 * is `1.9.1` to that record and passing review is not yet proven. See
 * `docs/platform-releases/versioning.md` § Four-part App Store versions.
 *
 * The fleet version (`apps/mobile/version.json`, tag `mobile/v1.9.1`, every OTA,
 * the binary) is one `x.y.z` for every school (SCH-2563). Apple refuses a new
 * upload under an App Store version that is already live, so an asset-only
 * rebuild of one school (name, icon, splash) after its 1.9.1 is live names only
 * the App Store version RECORD `1.9.1.1`, then `1.9.1.2`. The binary still says
 * 1.9.1. The fourth part:
 *
 *  - is an App Store resubmission counter only: never in version.json, git
 *    tags, OTAs or Linear;
 *  - is used only when Apple forces a new version, i.e. the app's record for
 *    the fleet version is live or otherwise closed;
 *  - counts per app, and resets when the fleet version moves (the next code
 *    release is 1.9.2, never 1.9.1.3 or 1.9.2.0);
 *  - never reaches Android, where only `versionCode` increases.
 *
 * Every helper here reads a name as `{ fleetVersion, resubmission }` so a
 * caller that needs the binary's version (build lookups, line resolution) never
 * filters on the record name.
 */

import type { Platform } from './platform'

/** An App Store version name read as the fleet version it ships and its resubmission count. */
export interface StoreVersionName {
  /** The `x.y.z` fleet version the binary carries. */
  fleetVersion: string
  /** The fourth part; 0 for a plain three-part name. */
  resubmission: number
}

/** A canonical non-negative integer: digits only, no leading zero. */
const CANONICAL_INT = /^(0|[1-9]\d{0,8})$/

/**
 * Parse an App Store version name. `1.9.1` reads `{ 1.9.1, 0 }` and `1.9.1.1`
 * reads `{ 1.9.1, 1 }`. Anything else is `null`: two parts (Apple's `1.0`
 * placeholder), five or more, a non-numeric or zero-padded segment, and a
 * fourth part of 0 (`1.9.1.0` is never written, rule 4).
 */
export function parseStoreVersionName(name: string | null | undefined): StoreVersionName | null {
  if (!name) return null
  const parts = name.trim().split('.')
  if (parts.length !== 3 && parts.length !== 4) return null
  if (!parts.every(part => CANONICAL_INT.test(part))) return null
  const resubmission = parts.length === 4 ? Number(parts[3]) : 0
  if (parts.length === 4 && resubmission === 0) return null
  return { fleetVersion: parts.slice(0, 3).join('.'), resubmission }
}

/** True for a four-part resubmission name (`1.9.1.1`), false for `1.9.1` and anything unreadable. */
export function isResubmissionStoreVersion(name: string | null | undefined): boolean {
  return (parseStoreVersionName(name)?.resubmission ?? 0) > 0
}

/** The fleet version a store version name ships (`1.9.1.1` → `1.9.1`), or `null` when unreadable. */
export function storeVersionFleetVersion(name: string | null | undefined): string | null {
  return parseStoreVersionName(name)?.fleetVersion ?? null
}

/**
 * The App Store version name an iOS push of `fleetVersion` should file under,
 * given every version name the app holds at Apple and the ones that are closed
 * (live, or otherwise past taking a new build).
 *
 * - No record of the fleet version, or its newest record still open: that
 *   record's name (the fleet version itself the first time). Reusing an open
 *   `1.9.1.1` matters: every store row of one push resolves the name again, and
 *   counting past an open record would ask Apple for a second one.
 * - Its newest record closed: `${fleetVersion}.${max + 1}`.
 *
 * Names of other fleet versions are ignored, so the count resets when the fleet
 * version moves. Returns `null` when `fleetVersion` is not a three-part `x.y.z`.
 */
export function nextStoreVersionName(
  fleetVersion: string,
  existingNamesForApp: readonly (string | null | undefined)[],
  closedNames: readonly (string | null | undefined)[]
): string | null {
  const fleet = parseStoreVersionName(fleetVersion)
  if (!fleet || fleet.resubmission !== 0) return null

  const closed = new Set(closedNames.filter((name): name is string => !!name).map(name => name.trim()))
  let newest: { name: string; resubmission: number } | null = null
  for (const name of [...existingNamesForApp, ...closed]) {
    const parsed = parseStoreVersionName(name)
    if (!name || !parsed || parsed.fleetVersion !== fleet.fleetVersion) continue
    if (!newest || parsed.resubmission > newest.resubmission)
      newest = { name: name.trim(), resubmission: parsed.resubmission }
  }
  if (!newest) return fleet.fleetVersion
  if (!closed.has(newest.name)) return newest.name
  return `${fleet.fleetVersion}.${newest.resubmission + 1}`
}

/**
 * Why a store version name must not reach this platform, or `null` when it may.
 * Android never carries a fourth part: Play orders builds by `versionCode`, and
 * `versionName` stays the fleet version (rule 6).
 */
export function storeVersionNameRefusal(platform: Platform, name: string | null | undefined): string | null {
  if (platform !== 'ANDROID' || !isResubmissionStoreVersion(name)) return null
  return `Android never carries a four-part version (${name}): Play orders builds by versionCode, and versionName stays the fleet version ${storeVersionFleetVersion(name)} (SCH-2593).`
}
